import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgOptimizedImage, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff } from '@ng-icons/lucide';
import { finalize } from 'rxjs/operators';
import { ZardButtonComponent } from '../../../shared/components/button';
import { ZardCardComponent } from '../../../shared/components/card';
import { ZardInputDirective } from '../../../shared/components/input';
import { UserService } from '../../../core/auth/services/user.service';
import { Errors } from '../../../core/models/errors.model';
import {UiLabel} from '../../../shared/components/label/label';

@Component({
  selector: 'app-auth-login',
  standalone: true,
  imports: [CommonModule, FormsModule, NgOptimizedImage, NgIcon, ZardButtonComponent, ZardCardComponent, ZardInputDirective, UiLabel],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideEye, lucideEyeOff })],
  templateUrl: './auth-login.component.html',
  styleUrl: './auth-login.component.css',
})
export class AuthLoginComponent {
   protected readonly router = inject(Router);
   private readonly userService = inject(UserService);

   protected readonly email = signal('');
   protected readonly password = signal('');

   protected readonly layoutOuter = 'min-h-screen flex flex-col md:flex-row bg-gray-50 md:bg-white';
   protected readonly sidePanelBgImage = 'https://images.unsplash.com/photo-1759863468440-85130c0dff28?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080';
   protected readonly layoutSidePanel =
     'hidden md:flex md:w-1/2 lg:w-5/12 relative overflow-hidden flex-col justify-between p-12 text-white';
   protected readonly layoutOverlay =
     'absolute inset-0 z-0 bg-gradient-to-t from-[#1A2E47] via-[#1A2E47]/50 to-transparent';

   protected readonly showPassword = signal(false);
   protected readonly isSubmitting = signal(false);
   protected readonly errors = signal<Errors | null>(null);
    // whether user attempted to submit (used to show validation messages)
    protected readonly submitted = signal(false);

   /**
    * Format error messages based on server response type:
    * - 422 (Validation): Show field-specific errors
    * - 409 (Conflict): Show details message
    * - 401 (Unauthorized): Show title or details message
    * - Other: Show generic errors object
    */
   protected readonly errorMessages = () => {
     const err = this.errors();
     if (!err) return [];

     const messages: string[] = [];

     // Case 1: Server validation errors (422) with fieldErrors
     if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
       for (const [field, fieldMessages] of Object.entries(err.fieldErrors)) {
         if (Array.isArray(fieldMessages)) {
           fieldMessages.forEach(msg => {
             messages.push(`${field}: ${msg}`);
           });
         }
       }
     }
     // Case 2: Conflict or other server errors (409, 401, etc.) with details or title
     else if (err.details) {
       messages.push(err.details);
     }
     else if (err.title) {
       messages.push(err.title);
     }
     // Case 3: Generic errors object
     else if (err.errors && Object.keys(err.errors).length > 0) {
       Object.entries(err.errors).forEach(([field, message]) => {
         if (field === 'message' || field === 'network') {
           messages.push(message);
         } else {
           messages.push(`${field}: ${message}`);
         }
       });
     }

     return messages;
   };

   protected handleLoginSubmit(): void {
        // show validation messages after first submit attempt
        this.submitted.set(true);

        if (!this.email() || !this.password() || this.isSubmitting()) {
          return;
        }

        this.errors.set(null);
        this.isSubmitting.set(true);

     this.userService
       .login({
         email: this.email().trim(),
         password: this.password(),
       })
       .pipe(finalize(() => this.isSubmitting.set(false)))
       .subscribe({
         next: () => {
           this.email.set('');
           this.password.set('');
           void this.router.navigate(['/setup/confirmation']);
         },
         error: (err: Errors) => this.errors.set(err),
       });
   }

    protected emailErrors(): string[] {
      const msgs: string[] = [];
      const val = this.email().trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (this.submitted() || val.length > 0) {
        if (val.length === 0) {
          msgs.push("L'adresse e-mail est requise.");
        } else if (!emailRegex.test(val)) {
          msgs.push("L'adresse e-mail n'est pas valide.");
        }
      }

      const err = this.errors();
      if (err?.fieldErrors && err.fieldErrors['email']) {
        msgs.push(...err.fieldErrors['email']);
      }

      return msgs;
    }

    protected passwordErrors(): string[] {
      const msgs: string[] = [];
      const val = this.password();

      if (this.submitted() || val.length > 0) {
        if (val.length === 0) {
          msgs.push('Le mot de passe est requis.');
        }
      }

      const err = this.errors();
      if (err?.fieldErrors && err.fieldErrors['password']) {
        msgs.push(...err.fieldErrors['password']);
      }

      return msgs;
    }

   protected handleLoginDemo(): void {
     this.email.set('demo@bailia.com');
     this.password.set('demo1234');
     this.handleLoginSubmit();
   }
}
